// src/enduser/eventgroups.jsx
// グループ一覧（公開）。ログイン不要の公開APIを呼ぶ。
// ?ring=N を付けると、そのリングの担当グループだけを表示する（リング一覧から開いたとき）。
import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import PubHeader from "./PubHeader";
import { formatEyebrow } from "./pubutil";
import "./eventcatalog.css";

const API_URL = import.meta.env.VITE_API_URL;

export default function EnduserEventGroups() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const ringParam = searchParams.get("ring") || "";

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["event-groups", "public", eventId, ringParam],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/api/public/events/${eventId}/groups/`, {
        params: ringParam ? { ring: ringParam } : {},
      });
      if (!res.data?.success) throw new Error("グループの取得に失敗しました");
      return res.data.data;
    },
    // 404（非公開・存在しない）と 400（ring が不正）はリトライしない
    retry: (count, err) => ![400, 404].includes(err?.response?.status) && count < 1,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const notFound = isError && error?.response?.status === 404;
  const groups = data?.groups || [];
  const ring = data?.ring || null;

  return (
    <div className="pubCat">
      <PubHeader
        to={ring ? `/events/${eventId}/rings` : `/events/${eventId}`}
        backLabel="戻る"
        title={ring ? `リング ${ring.ring_no}` : "グループ一覧"}
      />

      {isLoading ? (
        <div className="pubCatMsg">読み込み中...</div>
      ) : isError ? (
        <div className="pubCatMsg">
          {notFound
            ? "イベントが見つかりません"
            : `エラー: ${error?.response?.data?.message || error?.message || "グループの取得に失敗しました"}`}
          <br />
          <Link to="/top">イベント一覧へ戻る</Link>
        </div>
      ) : (
        <>
          <div className="pubCatHero">
            {ring ? (
              <div className="pubCatInner pubCatRingHero">
                <div className="pubCatRingBadgeLg">
                  <span className="pubCatRingLabel">RING</span>
                  <span className="pubCatRingNo">{ring.ring_no}</span>
                </div>
                <div className="pubCatRingInfo">
                  <div className="pubCatJLabel">審査員</div>
                  <div className="pubCatJName">{ring.judge_names.join(" ／ ")}</div>
                </div>
              </div>
            ) : (
              <div className="pubCatInner">
                <div className="pubCatEyebrow">{formatEyebrow(data.event.date)}</div>
                <h1 className="pubCatTitle">{data.event.title}</h1>
                <div className="pubCatRule" />
              </div>
            )}
          </div>

          <div className="pubCatInner pubCatBody">
            {groups.length === 0 ? (
              <div className="pubCatMsg">グループは、まだ公開されていません</div>
            ) : (
              <div className="pubCatGroups">
                {groups.map((g) => (
                  <Link
                    key={g.group_no}
                    to={`/events/${eventId}/groups/${g.group_no}`}
                    className="pubCatGroup"
                  >
                    <div className="pubCatGroupLeft">
                      {g.start_time && (
                        <span className="pubCatGroupTime">{g.start_time}〜</span>
                      )}
                      <span className="pubCatGroupName">
                        <span className="pubCatGroupNo">{g.group_no}G</span>
                        {g.name}
                      </span>
                      {!ring && g.ring_no && (
                        <span className="pubCatGroupSub">
                          リング{g.ring_no}　{g.judge_name}
                        </span>
                      )}
                    </div>
                    <span className="pubCatCnt">
                      <span className="pubCatCntN">{g.entry_count}</span>
                      <span className="pubCatCntL">頭</span>
                    </span>
                    <svg className="pubCatChev" aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B88A50" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
